import {
  IInventoryBranchDetails,
  IInventoryMOUDetails,
  IInventoryMSMEDetails,
  IInventoryOtherDetails,
  IInventoryPanDetails,
  IInventoryVendorBasicDetails,
  IInventoryVendorCreatePostBody,
} from "@/api_framework/api_modals/Inventory";
import { useInventoryVendorCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryVendorCreateAPI";
import { useInventoryVendorGetDetailsAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryVendorGetDetailsAPI";
import { useInventoryVendorUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryVendorUpdateAPI";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import VendorBasicDetailsFormComponent from "./VendorBasicDetailsFormComponent";
import VendorBranchDetailsFormComponent from "./VendorBranchDetailsFormComponent";
import VendorMOUDetailsFormComponent from "./VendorMOUDetailsFormComponent";
import VendorMSMEDetailsFormComponent from "./VendorMSMEDetailsFormComponent";
import VendorOtherDocumentsFormComponent from "./VendorOtherDocumentsFormComponent";
import VendorPanDetailsFormComponent from "./VendorPanDetailsFormComponent";
import clsx from "clsx";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

export interface IAddVendorBasicInfoFormFields extends Omit<IInventoryVendorBasicDetails, "payment_term"> {
  status: boolean;
  payment_term: string;
}
export interface IAddVendorPanDetailsFormFields extends IInventoryPanDetails {
  pan_attachment_file: FileList;
  pan_operational_certificate_attachment_file: FileList;
  compliance_194Q_attachment_file: FileList;
}
export interface IAddVendorMSMEDetailsFormFields extends IInventoryMSMEDetails {
  registered_status: string;
  declaration_attachment_file: FileList;
  msme_number: string;
}
export interface IAddVendorMOUDetailsFormFields extends IInventoryMOUDetails {
  mou_attachment_file: FileList;
}
export interface IAddVendorOtherDetailsFormFields extends IInventoryOtherDetails {
  attachment_file: FileList;
}
export interface IAddVendorsBrancDetailsFormFields {
  status: boolean;
  branches: IInventoryBranchDetails[];
}

const branchInitialDefaultState: IInventoryBranchDetails = {
  branch_name: "",
  branch_id: "",
  bank_details: {
    account_number: null,
    confirmed_account_number: null,
    bank_name: null,
    beneficiary_name: null,
    ifs_code: null,
  },
  billing_address: {
    address_line_1: "",
    address_line_2: "",
    city: "",
    pincode: "",
    state: "",
  },
  contact_details: {
    primary_phone: "",
    alternate_phone: "",
    email: "",
    name: "",
  },
  gst_certificate: {
    base_64_string: "",
    file_extension: "",
    file_name: "",
  },
  gst_number: "",
};

const VendorAddComponent = () => {
  const [URLSearchParams, _setParams] = useSearchParams();
  const [isForEdit, setIsForEdit] = useState<boolean>(false);
  const { fileListToBase64 } = useUtils();
  const updateVendorAPI = useInventoryVendorUpdateAPI();
  const navigate = useNavigate();
  const basicInfoFormHook = useForm<IAddVendorBasicInfoFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const panDetailsFormHook = useForm<IAddVendorPanDetailsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const msmeDetailsFormHook = useForm<IAddVendorMSMEDetailsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const mouDetailsFormHook = useForm<IAddVendorMOUDetailsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const otherDetailsFormHook = useForm<IAddVendorOtherDetailsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const branchDetailsFormHook = useForm<IAddVendorsBrancDetailsFormFields>({
    mode: "onChange",
    defaultValues: {
      status: false,
      branches: [
        {
          ...branchInitialDefaultState,
        },
      ],
    },
  });

  const branchesFieldArrayHook = useFieldArray<IAddVendorsBrancDetailsFormFields>({
    name: "branches",
    control: branchDetailsFormHook.control,
  });

  const { execute: createVendor } = useInventoryVendorCreateAPI();
  const { vendorDetails, execute: getVendorDetails } = useInventoryVendorGetDetailsAPI();
  const vendorID = URLSearchParams.get("vendor_id");
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (vendorID) {
      getVendorDetails(vendorID).then(() => {
        setIsForEdit(true);
      });
    }
  }, [vendorID]);

  const setBasicSection = () => {
    basicInfoFormHook.setValue("trade_name", vendorDetails?.trade_name);
    basicInfoFormHook.setValue("alternate_phone", vendorDetails?.alternate_phone);
    basicInfoFormHook.setValue("email", vendorDetails?.email);
    basicInfoFormHook.setValue("primary_phone", vendorDetails?.primary_phone);
    basicInfoFormHook.setValue("legal_name", vendorDetails?.legal_name);
    basicInfoFormHook.setValue("status", vendorDetails?.status);
    basicInfoFormHook.setValue("sap_code", vendorDetails?.sap_code ?? "");
    // shouldValidate: an old payment term outside 1-1000 shows its error as soon as the form loads
    basicInfoFormHook.setValue("payment_term", vendorDetails?.payment_term ? String(vendorDetails.payment_term) : "", {
      shouldValidate: true,
    });
  };

  const setPanSection = (panDetails: IInventoryPanDetails) => {
    panDetailsFormHook.setValue("pan_number", vendorDetails?.pan_details?.pan_number);
    panDetailsFormHook.setValue("pan_attachment", panDetails.pan_attachment);
    panDetailsFormHook.setValue("compliance_194Q_attachment", panDetails.compliance_194Q_attachment);
    panDetailsFormHook.setValue(
      "pan_operational_certificate_attachment",
      panDetails.pan_operational_certificate_attachment,
    );
  };

  const setMSMESection = (msme: IInventoryMSMEDetails) => {
    msmeDetailsFormHook.setValue("is_registered", msme?.is_registered);
    msmeDetailsFormHook.setValue("msme_number", msme?.msme_number);
    msmeDetailsFormHook.setValue("attachment", msme.attachment);
  };

  const setMOUSection = (mou: IInventoryMOUDetails) => {
    mouDetailsFormHook.setValue("attachment", mou?.attachment);
  };

  const setOtherDocumentSection = () => {
    otherDetailsFormHook.setValue("attachment", vendorDetails?.other_documents?.attachment);
  };

  const setVendorBranchSection = (branches: IInventoryBranchDetails[]) => {
    branches.forEach((branch, index) => {
      branchDetailsFormHook.setValue(`branches.${index}.branch_name`, branch?.branch_name);
      branchDetailsFormHook.setValue(`branches.${index}.branch_id`, branch?.branch_id);
      branchDetailsFormHook.setValue(`branches.${index}.gst_number`, branch?.gst_number);
      branchDetailsFormHook.setValue(
        `branches.${index}.billing_address.address_line_1`,
        branch?.billing_address?.address_line_1,
      );
      branchDetailsFormHook.setValue(
        `branches.${index}.billing_address.address_line_2`,
        branch?.billing_address?.address_line_2,
      );
      branchDetailsFormHook.setValue(`branches.${index}.billing_address.city`, branch?.billing_address?.city);
      branchDetailsFormHook.setValue(`branches.${index}.billing_address.state`, branch?.billing_address?.state);
      branchDetailsFormHook.setValue(`branches.${index}.billing_address.pincode`, branch?.billing_address?.pincode);
      branchDetailsFormHook.setValue(
        `branches.${index}.bank_details.account_number`,
        branch?.bank_details?.account_number,
      );
      branchDetailsFormHook.setValue(
        `branches.${index}.bank_details.beneficiary_name`,
        branch?.bank_details?.beneficiary_name,
      );
      branchDetailsFormHook.setValue(
        `branches.${index}.bank_details.confirmed_account_number`,
        branch?.bank_details?.confirmed_account_number,
      );
      branchDetailsFormHook.setValue(`branches.${index}.bank_details.bank_name`, branch?.bank_details?.bank_name);
      branchDetailsFormHook.setValue(`branches.${index}.bank_details.ifs_code`, branch?.bank_details?.ifs_code);
      branchDetailsFormHook.setValue(`branches.${index}.contact_details.name`, branch?.contact_details?.name);
      branchDetailsFormHook.setValue(`branches.${index}.contact_details.email`, branch?.contact_details?.email);
      branchDetailsFormHook.setValue(
        `branches.${index}.contact_details.primary_phone`,
        branch?.contact_details?.primary_phone,
      );
      branchDetailsFormHook.setValue(
        `branches.${index}.contact_details.alternate_phone`,
        branch?.contact_details?.alternate_phone,
      );

      branchDetailsFormHook.setValue(`branches.${index}.gst_certificate`, branch.gst_certificate);
    });
  };

  const setBasicFormDataForUpdate = () => {
    setBasicSection();
    setPanSection(vendorDetails.pan_details);
    setMSMESection(vendorDetails.msme_details);
    setMOUSection(vendorDetails?.mou_details);
    setOtherDocumentSection();
    setVendorBranchSection(vendorDetails?.branch_details);
  };

  useEffect(() => {
    if (vendorDetails && isForEdit) {
      setBasicFormDataForUpdate();
      branchDetailsFormHook.setValue("status", vendorDetails?.status ?? false);
    }
  }, [vendorDetails]);

  const constructBasiInfoPartialPayload = (data: IAddVendorBasicInfoFormFields): IInventoryVendorBasicDetails => {
    const sapCode = data.sap_code?.trim();
    const paymentTerm = data.payment_term?.toString().trim();
    return {
      ...data,
      alternate_phone: data.alternate_phone === "" ? null : data.alternate_phone,
      sap_code: sapCode ? sapCode : null,
      payment_term: paymentTerm ? Number(paymentTerm) : null,
    };
  };

  const constructPanDetailsPartialPayload = async (data: IAddVendorPanDetailsFormFields) => {
    const panAttachementBase64 = await fileListToBase64(data?.pan_attachment_file).then((images) => {
      return images;
    });
    const compliance194QAttachmentBase64 = await fileListToBase64(data?.compliance_194Q_attachment_file).then(
      (images) => {
        return images;
      },
    );
    const panOperationalCertificateAttachmentBase64 = await fileListToBase64(
      data?.pan_operational_certificate_attachment_file,
    ).then((images) => {
      return images;
    });
    const fields: IInventoryPanDetails = {
      pan_attachment:
        data?.pan_attachment_file.length > 0
          ? {
              ...panAttachementBase64[0],
            }
          : {
              ...data.pan_attachment,
            },
      compliance_194Q_attachment:
        data.compliance_194Q_attachment_file?.length > 0
          ? {
              ...compliance194QAttachmentBase64[0],
            }
          : {
              ...data.compliance_194Q_attachment,
            },
      pan_number: data.pan_number,
      pan_operational_certificate_attachment:
        data.pan_operational_certificate_attachment_file?.length > 0
          ? {
              ...panOperationalCertificateAttachmentBase64[0],
            }
          : {
              ...data.pan_operational_certificate_attachment,
            },
    };
    return fields;
  };

  const constructOtherDocumentsPartialPayload = async (data: IAddVendorOtherDetailsFormFields) => {
    const attachmentBase64 = await fileListToBase64(data?.attachment_file).then((images) => {
      return images;
    });
    const fields: IInventoryOtherDetails = {
      attachment: {
        ...attachmentBase64[0],
      },
    };
    const hasAttachment =
      Object.prototype.hasOwnProperty.call(fields, "attachment") && Object.keys(fields.attachment).length > 0;
    return hasAttachment ? fields : null;
  };

  const constructMOUDetailsPartialPayload = async (data: IAddVendorMOUDetailsFormFields) => {
    const attachmentBase64 = await fileListToBase64(data?.mou_attachment_file).then((images) => {
      return images;
    });
    const fields: IInventoryMOUDetails =
      data.mou_attachment_file?.length > 0
        ? {
            attachment: {
              ...attachmentBase64[0],
            },
          }
        : {
            attachment: {
              ...data.attachment,
            },
          };
    const hasAttachment =
      Object.prototype.hasOwnProperty.call(fields, "attachment") && Object.keys(fields.attachment).length > 0;
    return hasAttachment ? fields : null;
  };

  const constructBranchDetailsPartialPayload = async (data: IAddVendorsBrancDetailsFormFields) => {
    const transformedBranchDetails: IInventoryBranchDetails[] = [];
    for (let i = 0; i < data.branches.length; i++) {
      const files = data.branches[i].gst_certificate_attachment_file;
      const gstnCertificateAttachmentBase64 =
        files && files.length > 0 ? await fileListToBase64(files).then((files) => files[0]) : null;
      transformedBranchDetails.push({
        ...data.branches[i],
        branch_id: data.branches[i].branch_id,
        gst_certificate: gstnCertificateAttachmentBase64
          ? {
              base_64_string: gstnCertificateAttachmentBase64.base_64_string,
              file_extension: gstnCertificateAttachmentBase64.file_extension,
              file_name: gstnCertificateAttachmentBase64.file_name,
            }
          : { ...data?.branches[i]?.gst_certificate },
        contact_details: {
          ...data.branches[i].contact_details,
          alternate_phone:
            data.branches[i].contact_details.alternate_phone === ""
              ? null
              : data.branches[i].contact_details.alternate_phone,
        },
      });
    }
    return transformedBranchDetails;
  };

  const constructMSMEDetailsPartialPayload = async (data: IAddVendorMSMEDetailsFormFields) => {
    const attachmentBase64 = await fileListToBase64(data?.declaration_attachment_file).then((images) => {
      return images;
    });
    const fields: IInventoryMSMEDetails =
      data.declaration_attachment_file?.length > 0
        ? {
            attachment: {
              ...attachmentBase64[0],
            },
            is_registered: data?.registered_status === "yes" ? true : false,
            msme_number: data?.msme_number,
          }
        : {
            attachment: {
              ...data.attachment,
            },
            is_registered: data?.registered_status === "yes" ? true : false,
            msme_number: data?.msme_number,
          };
    return fields;
  };

  const constructFinalCreationPayload = async () => {
    const basicDataPartialPayload = constructBasiInfoPartialPayload(basicInfoFormHook.getValues());
    const panDetailsPartialPayload = await constructPanDetailsPartialPayload(panDetailsFormHook.getValues());
    const msmePartialPayload = await constructMSMEDetailsPartialPayload(msmeDetailsFormHook.getValues());
    const otherDetailsPartialPayload = await constructOtherDocumentsPartialPayload(otherDetailsFormHook.getValues());
    const mouDetailsPartialPayload = await constructMOUDetailsPartialPayload(mouDetailsFormHook.getValues());
    const branchDetailaPartialPayload = await constructBranchDetailsPartialPayload(branchDetailsFormHook?.getValues());
    const vendorCreationPayload: IInventoryVendorCreatePostBody = {
      ...basicDataPartialPayload,
      mou_details: mouDetailsPartialPayload,
      branch_details: branchDetailaPartialPayload,
      msme_details: msmePartialPayload,
      other_documents: otherDetailsPartialPayload,
      pan_details: panDetailsPartialPayload,
      status: branchDetailsFormHook.getValues("status"),
    };
    return vendorCreationPayload;
  };

  const setPanMediaAttachment = async () => {
    const response = await constructPanDetailsPartialPayload(panDetailsFormHook?.getValues());
    Object.keys(response)?.map((key: keyof IAddVendorPanDetailsFormFields) => {
      panDetailsFormHook.setValue(key, response[key]);
    });
  };

  const setMSMEMediaAttachment = async () => {
    const response = await constructMSMEDetailsPartialPayload(msmeDetailsFormHook.getValues());
    Object.keys(response).map((key: keyof IAddVendorMSMEDetailsFormFields) => {
      msmeDetailsFormHook.setValue(key, response[key]);
    });
  };

  useEffect(() => {
    setPanMediaAttachment();
  }, [
    panDetailsFormHook?.watch("pan_attachment_file"),
    panDetailsFormHook?.watch("compliance_194Q_attachment_file"),
    panDetailsFormHook?.watch("pan_operational_certificate_attachment_file"),
  ]);

  useEffect(() => {
    setMSMEMediaAttachment();
  }, [msmeDetailsFormHook.watch("declaration_attachment_file")]);

  const resetForm = () => {
    basicInfoFormHook.reset();
    panDetailsFormHook.reset();
    mouDetailsFormHook.reset();
    msmeDetailsFormHook.reset();
    otherDetailsFormHook.reset();
    branchDetailsFormHook.reset();
  };

  const handleSubmit = async () => {
    try {
      const isSapFieldsValid = await basicInfoFormHook.trigger(["sap_code", "payment_term"]);
      if (!isSapFieldsValid) return;
      setLoader(true);
      const createPayload = await constructFinalCreationPayload();
      if (vendorID) {
        const { status } = await updateVendorAPI(createPayload, vendorDetails?.vendor_id);
        if (status) {
          resetForm();
          setLoader(false);
          navigate("/app/inventory/vendors-list");
        } else {
          setLoader(false);
        }
      } else {
        await createVendor(createPayload).then(({ status }) => {
          if (status) {
            resetForm();
            setLoader(false);
            navigate("/app/inventory/vendors-list");
          } else {
            setLoader(false);
          }
        });
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const handelBackToVendorList = () => {
    navigate(`/app/inventory/vendors-list`);
  };

  return (
    <div className="relative mt-6 px-6">
      <div className="border-gray-200 pb-5 border-b">
        <h3 className="font-semibold text-base text-gray-900 leading-6">Vendor Setup</h3>
        <p className="mt-2 max-w-4xl text-gray-500 text-sm">Add vendor for the inventories</p>
      </div>
      <VendorBasicDetailsFormComponent basicInfoFormHook={basicInfoFormHook} />
      <VendorPanDetailsFormComponent isForEdit={isForEdit} panDetailsFormHook={panDetailsFormHook} />
      <VendorMSMEDetailsFormComponent isForEdit={isForEdit} msmeDetailsFormHook={msmeDetailsFormHook} />
      <VendorMOUDetailsFormComponent mouDetailsFormHook={mouDetailsFormHook} />
      <VendorOtherDocumentsFormComponent otherDetailsFormHook={otherDetailsFormHook} />
      <VendorBranchDetailsFormComponent
        isForEdit={isForEdit}
        branchDetailsFormHook={branchDetailsFormHook}
        branchesFieldArrayHook={branchesFieldArrayHook}
        initialState={branchInitialDefaultState}
      />
      <div className="bottom-0 right-0 sticky flex flex-row justify-end items-center gap-3 sm:gap-x-4 bg-white mt-8 px-2 py-3 border-t-2 w-full">
        <button type="button" className={SECONDARY_BUTTON} onClick={() => handelBackToVendorList()}>
          Cancel
        </button>
        <button
          type="button"
          disabled={loader}
          className={clsx(PRIMARY_BUTTON, "disabled:opacity-60 disabled:cursor-not-allowed")}
          onClick={() => handleSubmit()}
        >
          {loader ? "Loading....." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default VendorAddComponent;
